/**
 * MermaidDiagram - Interactive diagrams with drill-down to procurement data
 *
 * Supports:
 * - Network topology diagrams
 * - Architecture diagrams
 * - Flowcharts
 * - Click-to-expand for fullscreen view
 * - Click on nodes to drill-down to transactions
 *
 * Schema format:
 * {
 *   type: 'mermaid-diagram',
 *   code: 'graph LR\n  FW[Firewall] --> SW[Switch]',
 *   title: 'Network Architecture',
 *   featured: true,
 *   interactive: true,
 *   nodeData: {
 *     'FW': { nodeType: 'vendor_name', nodeValue: 'Check Point', spend: 120000 },
 *     'SW': { nodeType: 'vendor_name', nodeValue: 'Cisco', spend: 45000 }
 *   }
 * }
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2, Download, ExternalLink } from 'lucide-react';
import SankeyDrilldownAdapter from '../../../SankeyFlowChart/SankeyDrilldownAdapter';
import './MermaidDiagram.css';

// mermaid is ~450KB minified — before this change it was the single largest
// dependency in the main bundle, parsed on every page load for a diagram
// most sessions never render. Loaded on demand at first render instead.
let mermaidPromise = null;
const loadMermaid = () => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => m.default);
  }
  return mermaidPromise;
};

// Modern theme config matching global design system
const MERMAID_THEME = {
  primaryColor: '#3b82f6',
  primaryTextColor: '#ffffff',
  primaryBorderColor: '#2563eb',
  secondaryColor: '#e0f2fe',
  secondaryTextColor: '#0c4a6e',
  secondaryBorderColor: '#06b6d4',
  tertiaryColor: '#f4f4f5',
  tertiaryTextColor: '#3f3f46',
  tertiaryBorderColor: '#d4d4d8',
  lineColor: '#71717a',
  nodeBorder: '#d4d4d8',
  nodeTextColor: '#18181b',
  background: '#ffffff',
  mainBkg: '#ffffff',
  clusterBkg: 'rgba(59, 130, 246, 0.04)',
  clusterBorder: 'rgba(59, 130, 246, 0.2)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  fontSize: '14px',
  edgeLabelBackground: '#ffffff',
};

const DARK_THEME = {
  ...MERMAID_THEME,
  primaryColor: '#60a5fa',
  primaryBorderColor: '#3b82f6',
  secondaryColor: '#1e3a5f',
  tertiaryColor: '#27272a',
  tertiaryTextColor: '#e4e4e7',
  lineColor: '#a1a1aa',
  nodeBorder: '#52525b',
  nodeTextColor: '#fafafa',
  background: '#18181b',
  mainBkg: '#18181b',
  clusterBkg: 'rgba(59, 130, 246, 0.08)',
  clusterBorder: 'rgba(59, 130, 246, 0.3)',
  edgeLabelBackground: '#27272a',
};

// (mermaid.initialize happens in renderToContainer, per render, with the
// active theme — there is deliberately no module-level init so the library
// isn't loaded until a diagram actually renders.)

/**
 * Format currency for display
 */
function formatCurrency(value) {
  if (!value && value !== 0) return null;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

/**
 * MermaidDiagram Component
 */
export default function MermaidDiagram({
  code,
  title,
  theme = 'default',
  className = '',
  expandable = true,
  featured = false,
  interactive = false,
  nodeData = {},      // Maps node IDs to { nodeType, nodeValue, spend, componentId, ... }
  department = null,
  departmentId = null,
  onAction,           // Callback for custom actions
}) {
  const containerRef = useRef(null);
  const expandedContainerRef = useRef(null);
  const [error, setError] = useState(null);
  const [rendered, setRendered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoom, setZoom] = useState(1); // Fit first; user zooms in for detail
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Check if we have interactive node data
  const hasInteractiveNodes = interactive && Object.keys(nodeData).length > 0;

  // Attach click handlers to SVG nodes after render
  const attachNodeHandlers = useCallback((container) => {
    if (!container || !hasInteractiveNodes) return;

    const svgNodes = container.querySelectorAll('.node');

    svgNodes.forEach(node => {
      // Get the node ID from the element
      const nodeId = node.id?.replace('flowchart-', '').split('-')[0] ||
                     node.getAttribute('data-id') ||
                     node.querySelector('.nodeLabel')?.textContent?.trim();

      // Check if we have data for this node
      const data = nodeData[nodeId];
      if (!data) return;

      // Mark as interactive
      node.classList.add('mermaid-node--interactive');
      node.style.cursor = 'pointer';

      // Add spend badge if available
      if (data.spend) {
        const badge = document.createElement('div');
        badge.className = 'mermaid-node__spend-badge';
        badge.textContent = formatCurrency(data.spend);

        // Position relative to node
        const rect = node.querySelector('rect, polygon, circle');
        if (rect) {
          const bbox = rect.getBBox?.() || {};
          badge.style.left = `${bbox.x + bbox.width - 10}px`;
          badge.style.top = `${bbox.y - 8}px`;
        }

        // Append to parent SVG or node
        node.appendChild(badge);
      }

      // Click handler
      node.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedNode({
          nodeType: data.nodeType || 'vendor_name',
          nodeValue: data.nodeValue || data.label || nodeId,
          ...data,
        });
      });

      // Hover handlers
      node.addEventListener('mouseenter', () => {
        setHoveredNode({
          id: nodeId,
          ...data,
        });
      });

      node.addEventListener('mouseleave', () => {
        setHoveredNode(null);
      });
    });
  }, [hasInteractiveNodes, nodeData]);

  // Render diagram to a container
  const renderToContainer = useCallback(async (container, diagramCode) => {
    if (!container || !diagramCode) return;

    try {
      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark' || theme === 'dark';

      const mermaid = await loadMermaid();
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        // 'strict' — chat diagram source is untrusted model output (FE-2).
        securityLevel: 'strict',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
          padding: 20,
          nodeSpacing: 50,
          rankSpacing: 60,
        },
        themeVariables: isDarkMode ? DARK_THEME : MERMAID_THEME,
      });

      const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      container.innerHTML = '';

      const { svg } = await mermaid.render(id, diagramCode);
      container.innerHTML = svg;

      // Attach click handlers for interactive nodes
      attachNodeHandlers(container);

      return true;
    } catch (err) {
      console.error('[MermaidDiagram] Render error:', err);
      return false;
    }
  }, [theme, attachNodeHandlers]);

  useEffect(() => {
    if (!code || !containerRef.current) return;

    const render = async () => {
      setError(null);
      setRendered(false);
      const success = await renderToContainer(containerRef.current, code);
      if (success) {
        setRendered(true);
      } else {
        setError('Failed to render diagram');
      }
    };

    render();
  }, [code, renderToContainer]);

  // Re-render when expanded
  useEffect(() => {
    if (isExpanded && expandedContainerRef.current && code) {
      renderToContainer(expandedContainerRef.current, code);
    }
  }, [isExpanded, code, renderToContainer]);

  const handleExpand = (e) => {
    // Don't expand if clicking on an interactive node
    if (e.target.closest('.mermaid-node--interactive')) return;
    setIsExpanded(true);
    setZoom(1);
  };

  const handleClose = () => {
    setIsExpanded(false);
    setZoom(1);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);

  const handleDownload = () => {
    const container = isExpanded ? expandedContainerRef.current : containerRef.current;
    if (!container) return;
    const svg = container.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'diagram'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCloseDrilldown = () => {
    setSelectedNode(null);
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedNode) {
          setSelectedNode(null);
        } else if (isExpanded) {
          handleClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isExpanded, selectedNode]);

  if (!code) {
    return (
      <div className={`mermaid-diagram mermaid-diagram--empty ${className}`}>
        <p>No diagram code provided</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`mermaid-diagram ${expandable ? 'mermaid-diagram--expandable' : ''} ${featured ? 'mermaid-diagram--featured' : ''} ${hasInteractiveNodes ? 'mermaid-diagram--interactive' : ''} ${className}`}
        onClick={expandable ? handleExpand : undefined}
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={expandable ? (e) => e.key === 'Enter' && handleExpand(e) : undefined}
      >
        <div className="mermaid-diagram__header">
          <div className="mermaid-diagram__header-left">
            {title && <h3 className="mermaid-diagram__title">{title}</h3>}
            {hasInteractiveNodes && (
              <span className="mermaid-diagram__interactive-badge">
                <ExternalLink size={12} />
                Click nodes to drill down
              </span>
            )}
          </div>
          {expandable && (
            <button className="mermaid-diagram__expand-hint" title="Click to expand">
              <Maximize2 size={14} />
              <span>Expand</span>
            </button>
          )}
        </div>

        {error ? (
          <div className="mermaid-diagram__error">
            <span className="mermaid-diagram__error-icon">⚠️</span>
            <span>Failed to render diagram: {error}</span>
            <pre className="mermaid-diagram__code">{code}</pre>
          </div>
        ) : (
          <div
            ref={containerRef}
            className={`mermaid-diagram__container ${rendered ? 'mermaid-diagram__container--rendered' : ''}`}
          />
        )}

        {/* Hover tooltip for nodes */}
        {hoveredNode && (
          <div className="mermaid-diagram__tooltip">
            <div className="mermaid-diagram__tooltip-title">{hoveredNode.nodeValue || hoveredNode.id}</div>
            {hoveredNode.spend && (
              <div className="mermaid-diagram__tooltip-spend">{formatCurrency(hoveredNode.spend)}</div>
            )}
            <div className="mermaid-diagram__tooltip-hint">Click to view transactions</div>
          </div>
        )}
      </div>

      {/* Expanded Modal View */}
      {isExpanded && (
        <div className="mermaid-diagram-modal" onClick={handleClose}>
          <div className="mermaid-diagram-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="mermaid-diagram-modal__header">
              <div className="mermaid-diagram-modal__header-left">
                <h2 className="mermaid-diagram-modal__title">{title || 'Diagram'}</h2>
                {hasInteractiveNodes && (
                  <span className="mermaid-diagram__interactive-badge mermaid-diagram__interactive-badge--modal">
                    <ExternalLink size={12} />
                    Click any node to see transactions
                  </span>
                )}
              </div>
              <div className="mermaid-diagram-modal__controls">
                <button onClick={handleZoomOut} title="Zoom out" disabled={zoom <= 0.5}>
                  <ZoomOut size={18} />
                </button>
                <button onClick={handleResetZoom} title="Reset zoom" className="mermaid-diagram-modal__zoom-level">
                  {Math.round(zoom * 100)}%
                </button>
                <button onClick={handleZoomIn} title="Zoom in" disabled={zoom >= 3}>
                  <ZoomIn size={18} />
                </button>
                <button onClick={handleDownload} title="Download SVG">
                  <Download size={18} />
                </button>
                <button onClick={handleClose} title="Close (Esc)" className="mermaid-diagram-modal__close">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="mermaid-diagram-modal__body">
              <div
                ref={expandedContainerRef}
                className="mermaid-diagram-modal__diagram"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Drill-down Modal */}
      {selectedNode && (
        <SankeyDrilldownAdapter
          node={selectedNode}
          isOpen={!!selectedNode}
          onClose={handleCloseDrilldown}
          department={department}
          departmentId={departmentId}
        />
      )}
    </>
  );
}

/**
 * NetworkDiagram - Specialized component for network topology
 */
export function NetworkDiagram({ nodes, connections, title, className, department, departmentId }) {
  const generateMermaidCode = () => {
    if (!nodes || !connections) return '';

    let code = 'graph TB\n';

    const categories = {};
    nodes.forEach(node => {
      const cat = node.category || 'Other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(node);
    });

    Object.entries(categories).forEach(([category, categoryNodes]) => {
      code += `  subgraph ${category.replace(/\s+/g, '_')}["${category}"]\n`;
      categoryNodes.forEach(node => {
        const shape = getNodeShape(node.type);
        code += `    ${node.id}${shape[0]}${node.label}${shape[1]}\n`;
      });
      code += '  end\n';
    });

    connections.forEach(conn => {
      const arrow = conn.bidirectional ? ' <--> ' : ' --> ';
      const label = conn.label ? `|${conn.label}|` : '';
      code += `  ${conn.from}${arrow}${label}${conn.to}\n`;
    });

    return code;
  };

  const getNodeShape = (type) => {
    switch (type) {
      case 'firewall': return ['{{', '}}'];
      case 'server': return ['[/', '/]'];
      case 'database': return ['[(', ')]'];
      case 'cloud': return ['([', '])'];
      case 'user': return ['((', '))'];
      case 'router': return ['{', '}'];
      default: return ['[', ']'];
    }
  };

  // Build nodeData from nodes array
  const nodeData = {};
  nodes?.forEach(node => {
    if (node.vendor || node.spend) {
      nodeData[node.id] = {
        nodeType: 'vendor_name',
        nodeValue: node.vendor || node.label,
        spend: node.spend,
        componentId: node.componentId,
        label: node.label,
      };
    }
  });

  return (
    <MermaidDiagram
      code={generateMermaidCode()}
      title={title}
      className={`network-diagram ${className || ''}`}
      interactive={Object.keys(nodeData).length > 0}
      nodeData={nodeData}
      department={department}
      departmentId={departmentId}
      featured
    />
  );
}

/**
 * ArchitectureDiagram - For system architecture diagrams
 */
export function ArchitectureDiagram({ layers, title, className, department, departmentId }) {
  const generateMermaidCode = () => {
    if (!layers) return '';

    let code = 'graph TB\n';

    layers.forEach((layer, idx) => {
      code += `  subgraph Layer${idx}["${layer.name}"]\n`;
      code += `    direction LR\n`;
      layer.components?.forEach(comp => {
        code += `    ${comp.id}["${comp.label}"]\n`;
      });
      code += '  end\n';
    });

    for (let i = 0; i < layers.length - 1; i++) {
      const currentLayer = layers[i];
      const nextLayer = layers[i + 1];
      if (currentLayer.components?.[0] && nextLayer.components?.[0]) {
        code += `  ${currentLayer.components[0].id} --> ${nextLayer.components[0].id}\n`;
      }
    }

    return code;
  };

  // Build nodeData from layers
  const nodeData = {};
  layers?.forEach(layer => {
    layer.components?.forEach(comp => {
      if (comp.vendor || comp.spend) {
        nodeData[comp.id] = {
          nodeType: 'vendor_name',
          nodeValue: comp.vendor || comp.label,
          spend: comp.spend,
          componentId: comp.componentId,
          label: comp.label,
        };
      }
    });
  });

  return (
    <MermaidDiagram
      code={generateMermaidCode()}
      title={title}
      className={`architecture-diagram ${className || ''}`}
      interactive={Object.keys(nodeData).length > 0}
      nodeData={nodeData}
      department={department}
      departmentId={departmentId}
      featured
    />
  );
}
